import React, { useCallback, useRef } from 'react'
import { observer } from 'mobx-react'
import classNames from 'classnames'

import { EloCard } from '@elo-ui/components/elo-card'
import { EloRadioButton } from '@elo-ui/components/elo-radio-button'
import { getPaymentFormsLabels } from '@elo-kit/constants/paymentSetting.constants'
import { EloTooltip } from '@elo-ui/components/elo-tooltip'
import { EloAccordion } from '@elo-ui/components/elo-accordion'
import { PaymentMethodSlider } from 'shared/components/purchase-methods/payment-method-slider/PaymentMethodSlider'
import NoData from 'ui/no-data/NoData'
import I18n from '@elo-kit/components/i18n'

import { createImg, getPaymentMethodIcon } from 'utils/paymentSettingShared.utils'

import { PAYER_FORM_TYPE, PAYMENT_FORMS, PAYMENT_LOGOS_LIST_HEIGHT } from 'constants/paymentSettingShared.constants'

import { useShopStores } from 'shop/hooks/use-store'

interface Props {
  paymentMethodsToShow: string[]
  activeMethodBody: () => React.ReactNode
  activeMethod: string
  allowedMethods: string[]
  googleAvailability: {
    googlePay: boolean
  }
  appleAvailability: {
    applePay: boolean
  }
  setActiveMethod: (method: string) => void
  cardProvider: string
  selectPaymentMethod: (method: string) => void
}

export const CheckoutPaymentMethods: React.FC<Props> = observer((props) => {
  const {
    paymentMethodsToShow,
    activeMethodBody,
    activeMethod,
    allowedMethods,
    googleAvailability,
    appleAvailability,
    setActiveMethod,
    cardProvider,
    selectPaymentMethod,
  } = props

  const { themeStore, paymentStore, ordersStore, trackingUserEventsStore } = useShopStores()

  const scrollWrapperRef = useRef(null)

  const preferredOrFirstPaymentMethod = paymentMethodsToShow[0]
  const paymentMethodsWithoutFirst = paymentMethodsToShow
    .filter((method) => method !== preferredOrFirstPaymentMethod)
    .slice(0, 3)
  const restPaymentMethods = paymentMethodsToShow.filter(
    (method) => method !== preferredOrFirstPaymentMethod && !paymentMethodsWithoutFirst.includes(method)
  )
  const isOneMethod = paymentMethodsWithoutFirst.length === 1

  const color = themeStore && themeStore?.ppTemplate?.theme?.paymentPageColor

  const isMethodWithoutInfo = (method) =>
    [PAYMENT_FORMS.sofort, PAYMENT_FORMS.googlePay, PAYMENT_FORMS.applePay, PAYMENT_FORMS.bankWire].includes(method)

  const memoActiveMethodBody = useCallback(
    () => <div className='new-paymethod-content new-paymethod-content--with-bottom-margin'>{activeMethodBody()}</div>,
    [activeMethod]
  )

  const newPaymentMethods = (method) => (isMethodWithoutInfo(method) ? null : memoActiveMethodBody())

  const isActive = (method) => method === activeMethod

  const getPaymentMethod = (method: string, firstMethod = false, isOneMethod = false) => {
    const isCard = method === PAYMENT_FORMS.card

    const isKlarnaDisabled =
      method === PAYMENT_FORMS.klarna &&
      (paymentStore.payerForms?.formType === PAYER_FORM_TYPE.business ||
        ordersStore.data?.payerData?.formType === PAYER_FORM_TYPE.business)

    const isPaymethodDisabled =
      (activeMethod === PAYMENT_FORMS.applePay && !appleAvailability?.applePay) ||
      (activeMethod === PAYMENT_FORMS.googlePay && !googleAvailability?.googlePay) ||
      isKlarnaDisabled

    if (isKlarnaDisabled && activeMethod === PAYMENT_FORMS.klarna) {
      if (allowedMethods.length > 1) {
        setActiveMethod(allowedMethods.find((method) => method !== PAYMENT_FORMS.klarna))
      } else {
        setActiveMethod('')
      }
    }

    const iconSrc = getPaymentMethodIcon(method, cardProvider, isCard && !firstMethod)
    const getImg = (icon) =>
      createImg({
        paymentForm: method,
        className: `${method}-img ${I18n.locale} ${cardProvider}`,
        src: icon,
        style: firstMethod ? { height: PAYMENT_LOGOS_LIST_HEIGHT[method] } : {},
      })

    const showOrSeparator = firstMethod && paymentMethodsToShow.length > 1

    return (
      <label key={method} htmlFor={`method_${method}`} className='payment-method-label'>
        <EloCard
          type='selectable'
          className={classNames({ 'payment-method-card': firstMethod })}
          contentClassName='elo-card__content--without-margin'
          selected={`method_${method}` === `method_${activeMethod}`}
          handleSelect={() => selectPaymentMethod(method)}
        >
          <div className='payment-method-card__content'>
            {firstMethod || isOneMethod ? (
              <>
                <div>
                  <EloRadioButton
                    checked={`method_${method}` === `method_${activeMethod}`}
                    onChange={() => {}}
                    disabled={isPaymethodDisabled}
                    className={I18n.locale}
                    borderColor={color}
                    id={`method_${method}`}
                  >
                    {getPaymentFormsLabels()[method]}
                  </EloRadioButton>
                </div>
                <div className='payment-method-card__logos'>
                  {Array.isArray(iconSrc) ? iconSrc.map((icon) => getImg(icon)) : getImg(iconSrc)}
                </div>
              </>
            ) : (
              <div className='payment-method-card__full-width'>
                <div className='payment-main-methods__logos'>
                  {Array.isArray(iconSrc) ? iconSrc.map((icon) => getImg(icon)) : getImg(iconSrc)}
                  {isCard && <span>+2</span>}
                </div>
                <div>
                  <EloRadioButton
                    checked={`method_${method}` === `method_${activeMethod}`}
                    onChange={() => {}}
                    disabled={isPaymethodDisabled}
                    className={I18n.locale}
                    borderColor={color}
                    id={`method_${method}`}
                  >
                    <EloTooltip withOverflow disabled content={getPaymentFormsLabels()[method]}>
                      {getPaymentFormsLabels()[method]}
                    </EloTooltip>
                  </EloRadioButton>
                </div>
              </div>
            )}
          </div>
        </EloCard>

        {firstMethod && isActive(method) && newPaymentMethods(method)}

        {showOrSeparator && (
          <div className='payment-method-visual-separation'>
            <div />
            {I18n.t('react.shared.or')}
            <div />
          </div>
        )}
      </label>
    )
  }

  return (
    <div className='paymethods new-paymethods'>
      {!paymentMethodsToShow.length && <NoData message={I18n.t('react.shared.payment_methods.no_available_method')} />}

      {getPaymentMethod(preferredOrFirstPaymentMethod, true)}

      <div className='payment-mobile-methods'>
        <div className='scroll-wrapper' ref={scrollWrapperRef}>
          {[...paymentMethodsWithoutFirst, ...restPaymentMethods].map((method) =>
            getPaymentMethod(method, false, isOneMethod)
          )}
        </div>
        <PaymentMethodSlider
          itemsLength={paymentMethodsToShow.length}
          scrollWrapperRef={scrollWrapperRef}
          itemWidth={144}
          itemGap={16}
        />
      </div>

      <div className='payment-methods-desktop-tablet'>
        <div className='payment-main-methods'>
          {paymentMethodsWithoutFirst.map((method) => getPaymentMethod(method))}
        </div>
      </div>

      {paymentMethodsWithoutFirst.includes(activeMethod) && newPaymentMethods(activeMethod)}

      <div className='payment-methods-desktop-tablet'>
        {!!restPaymentMethods.length && (
          <div className='payment-method-accordion'>
            <EloAccordion
              size='medium'
              items={[
                {
                  headline: I18n.t('react.shop.payment.show_more_options'),
                  content: (
                    <div className='payment-rest-methods'>
                      {restPaymentMethods.map((method) => getPaymentMethod(method))}
                    </div>
                  ),
                },
              ]}
              onItemToggle={(_, isOpened) =>
                trackingUserEventsStore?.logEvent({
                  pageType: 'shop_checkout',
                  eventType: 'payment_method_accordion_change',
                  payload: { isOpened },
                })
              }
            />
          </div>
        )}
      </div>

      {restPaymentMethods.includes(activeMethod) && newPaymentMethods(activeMethod)}
    </div>
  )
})
